import { ErrorInfo } from "react";
import { AppError, ErrorCode } from "../types/error";

export const createAppError = (
  message: string,
  code?: ErrorCode,
  statusCode?: number,
  isRetryable = false
): AppError => {
  const error = new Error(message) as AppError;
  error.code = code;
  error.statusCode = statusCode;
  error.isRetryable = isRetryable;
  return error;
};

export const handleApiError = (error: unknown): AppError => {
  if (error instanceof Error) {
    // Check if it's already an AppError
    if ("code" in error) {
      return error as AppError;
    }

    // Handle fetch/network errors
    if (error.message.includes("fetch")) {
      return createAppError("Network error. Please check your connection.", "NETWORK_ERROR", 0, true);
    }

    // Handle authentication errors
    if (error.message.toLowerCase().includes("unauthorized")) {
      return createAppError("Authentication failed. Please log in again.", "AUTH_ERROR", 401, false);
    }

    // Generic error
    return createAppError(error.message, "GENERIC_ERROR", 500, true);
  }

  // Handle non-Error objects
  return createAppError("An unexpected error occurred", "UNKNOWN_ERROR", 500, true);
};

export const logError = (error: Error, errorInfo?: ErrorInfo): void => {
  // In development, log to console
  if (import.meta.env.DEV) {
    console.group("🚨 Application Error");
    console.error("Error:", error);
    if (errorInfo) {
      console.error("Component Stack:", errorInfo.componentStack);
    }
    console.groupEnd();
  }

  // In production, you might want to send to an error reporting service
  // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
};

export const isRetryableError = (error: unknown): boolean => {
  if (error && typeof error === "object" && "isRetryable" in error) {
    return (error as AppError).isRetryable || false;
  }
  return false;
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "An unexpected error occurred";
};
